import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { IconButton } from './Button';
import { X } from './icons';

export interface ModalProps {
  title: ReactNode;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 's' | 'm' | 'l';
  /** rendered as a side drawer instead of a centred card */
  drawer?: boolean;
  /** the element to focus first (default: the first focusable inside) */
  initialFocus?: 'first' | 'none';
  className?: string;
  describedBy?: string;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The open modals, outermost first: only the topmost answers Escape and traps Tab (a reason prompt over a drawer closes alone). */
const openModals: symbol[] = [];

/** A dialog: focus-trapped, labelled by its title, closed by Escape, the close button and the backdrop (spec section 2.2). */
export function Modal({ title, open, onClose, children, footer, size = 's', drawer = false, initialFocus = 'first', className = '', describedBy }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const previous = useRef<Element | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose; // owners pass inline closures: the trap must not re-run (and move focus) on their every render

  useEffect(() => {
    if (!open) return;
    previous.current = document.activeElement;
    const el = ref.current;
    if (initialFocus === 'first') {
      const first = el?.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? el)?.focus();
    }
    const token = Symbol('modal');
    openModals.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (openModals[openModals.length - 1] !== token) return; // a modal above this one handles the keys
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && el) {
        const nodes = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null || n === document.activeElement);
        if (nodes.length === 0) return;
        const first = nodes[0]!;
        const last = nodes[nodes.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const at = openModals.indexOf(token);
      if (at >= 0) openModals.splice(at, 1);
      (previous.current as HTMLElement | null)?.focus?.();
    };
  }, [open, initialFocus]);

  if (!open) return null;
  const body = (
    <div ref={ref} className={drawer ? `drawer ${className}` : `modal ${size === 'm' ? 'modal-m' : size === 'l' ? 'modal-l' : ''} ${className}`} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={describedBy} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
      <header className="modal-header">
        <h2 id={titleId}>{title}</h2>
        <IconButton label="Close" onClick={onClose}>
          <X aria-hidden="true" />
        </IconButton>
      </header>
      <div className="modal-body">{children}</div>
      {footer && <footer className="modal-footer">{footer}</footer>}
    </div>
  );
  if (drawer) {
    return (
      <>
        <div className="drawer-backdrop" onClick={onClose} data-testid="modal-backdrop" />
        {body}
      </>
    );
  }
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop">
      {body}
    </div>
  );
}

/**
 * A modal's open state as a URL query parameter (`?doc=<id>`): opening pushes a history entry, so the back button closes
 * it and the URL is a deep link (spec section 3.1, Q-D-22).
 */
export function useUrlParam(key: string): { value: string | null; open: (value: string, extra?: Record<string, string | null>) => void; close: () => void; set: (patch: Record<string, string | null>) => void } {
  const [params, setParams] = useSearchParams();
  const value = params.get(key);
  const open = useCallback(
    (v: string, extra: Record<string, string | null> = {}) => {
      const next = new URLSearchParams(params);
      next.set(key, v);
      for (const [k, val] of Object.entries(extra)) {
        if (val === null) next.delete(k);
        else next.set(k, val);
      }
      setParams(next);
    },
    [params, setParams, key],
  );
  const close = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete(key);
    next.delete('tab');
    setParams(next);
  }, [params, setParams, key]);
  const set = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      for (const [k, val] of Object.entries(patch)) {
        if (val === null) next.delete(k);
        else next.set(k, val);
      }
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  return { value, open, close, set };
}
